import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PageHead from "@/components/PageHead";
import { LogIn } from "lucide-react";

export default function AdminLogin() {
  const { admin, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const from = location.state?.from || "/admin/journal";

  if (loading) {
    return (
      <section className="mx-auto max-w-[560px] px-6 md:px-10 py-24" data-testid="admin-login-loading">
        <p className="font-body text-ink-soft">Checking session…</p>
      </section>
    );
  }
  if (admin) return <Navigate to={from} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setError(
          typeof detail === "string"
            ? detail
            : "Too many attempts. Try again in 15 minutes."
        );
      } else if (status === 401) {
        setError("Invalid email or password.");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page">
      <PageHead
        title="Admin Login — Redkey Web Design"
        description="Admin login for Redkey Web Design blog management."
        canonical="https://redkeyweb.design/admin/login"
      />

      <section className="mx-auto max-w-[560px] px-6 md:px-10 pt-16 md:pt-24 pb-24">
        <p className="eyebrow">§ Admin · Blog</p>
        <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02]">
          Sign <em className="not-italic text-brand-red">in.</em>
        </h1>
        <p className="mt-4 max-w-prose text-ink-soft">
          Restricted access. This portal manages Redkey Web Design blog
          entries.
        </p>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="mt-10 space-y-6 border hairline-strong bg-paper p-6 md:p-8"
        >
          <div>
            <label htmlFor="admin-email" className="block form-label">
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-login-email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full bg-transparent border-b hairline-strong focus:border-electric outline-none py-3 text-lg font-body"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block form-label">
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full bg-transparent border-b hairline-strong focus:border-electric outline-none py-3 text-lg font-body"
            />
          </div>

          {error && (
            <p
              className="font-body text-sm text-brand-red-ink border border-brand-red-ink/40 bg-brand-red-ink/5 px-3 py-2"
              data-testid="admin-login-error"
              role="alert"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || !email || !password}
            data-testid="admin-login-submit"
            className="inline-flex items-center justify-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 disabled:opacity-60 font-ui"
          >
            <LogIn size={16} aria-hidden />
            <span>{submitting ? "Signing in…" : "Sign in"}</span>
          </button>

          <p className="font-ui text-[11px] text-label tracking-widest">
            5 FAILED ATTEMPTS PER 15 MINUTES = LOCKOUT
          </p>
        </form>
      </section>
    </div>
  );
}
